import React, { useEffect, useRef } from 'react';
import {
  IconDashboard, IconMessages, IconUsers, IconSpeakerphone, IconTicket,
  IconDeviceMobile, IconLanguage, IconRefresh, IconBell,
  IconChartArrowsVertical, IconHeadset, IconPlayerPlay, IconPlayerStop,
  IconRotateClockwise
} from '@tabler/icons-react';
import { AGENTS } from '../data/industries';
import Dashboard from './Dashboard';
import Conversations from './Conversations';
import CRM from './CRM';
import Marketing from './Marketing';
import Tickets from './Tickets';
import InteractiveDemo from './InteractiveDemo';

const NAV = [
  { id: 'dashboard', icon: IconDashboard, key: 'dashboard' },
  { id: 'conversations', icon: IconMessages, key: 'conversations' },
  { id: 'crm', icon: IconUsers, key: 'crm' },
  { id: 'marketing', icon: IconSpeakerphone, key: 'marketing' },
  { id: 'support', icon: IconTicket, key: 'support' },
  { id: '__divider__' },
  { id: 'interactive', icon: IconDeviceMobile, key: 'interactiveDemo', pulse: true },
];

const AGENT_ICONS = {
  abdullah: IconChartArrowsVertical,
  latifa: IconSpeakerphone,
  khalid: IconHeadset,
};

export default function AppShell({
  lang, t, industry, page, setPage, demoPhase, setDemoPhase,
  demoPhone, setDemoPhone, onToggleLang, onChangeIndustry, notifications, notify
}) {
  const isRtl = lang === 'ar';

  const demoTimers = useRef([]);

  const startDemo = () => {
    if (demoPhase === 0) {
      setPage('conversations');
      setDemoPhase(1);
    }
  };

  // Auto-navigate to conversations page when demo phase changes
  useEffect(() => {
    if (demoPhase >= 1 && demoPhase <= 3) {
      setPage('conversations');
    }
    return () => {
      demoTimers.current.forEach(clearTimeout);
      demoTimers.current = [];
    };
  }, [demoPhase]);
  const resetDemo = () => setDemoPhase(0);

  const phaseLabels = [t.salesFlow, t.supportFlow, t.marketingFlow];

  const renderPage = () => {
    switch (page) {
      case 'dashboard': return <Dashboard lang={lang} t={t} industry={industry} demoPhase={demoPhase} demoPhone={demoPhone} notify={notify} />;
      case 'conversations': return <Conversations lang={lang} t={t} industry={industry} demoPhase={demoPhase} setDemoPhase={setDemoPhase} notify={notify} setPage={setPage} />;
      case 'crm': return <CRM lang={lang} t={t} industry={industry} demoPhase={demoPhase} />;
      case 'marketing': return <Marketing lang={lang} t={t} industry={industry} demoPhase={demoPhase} notify={notify} />;
      case 'support': return <Tickets lang={lang} t={t} industry={industry} demoPhase={demoPhase} notify={notify} />;
      case 'interactive': return <InteractiveDemo lang={lang} t={t} industry={industry} notify={notify} onChangeIndustry={onChangeIndustry} setDemoPhone={setDemoPhone} />;
      default: return <Dashboard lang={lang} t={t} industry={industry} demoPhase={demoPhase} demoPhone={demoPhone} notify={notify} />;
    }
  };

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* Sidebar */}
      <aside style={{
        width: 220, background: '#1A1A2E', color: '#fff', display: 'flex', flexDirection: 'column',
        flexShrink: 0, overflow: 'hidden'
      }}>
        {/* Logo */}
        <div style={{ padding: '20px 16px 8px', borderBottom: '1px solid rgba(255,255,255,0.08)', textAlign: 'center' }}>
          <img src="/logo_white.png" alt="tkana" style={{ height: 28, objectFit: 'contain', marginBottom: 4 }} />
          <div style={{ color: '#7B4FAE', fontSize: 10, letterSpacing: 2, fontWeight: 600, marginTop: 2 }}>AIDA PLATFORM</div>
        </div>

        {/* Industry badge */}
        <div style={{
          margin: '12px 12px 0', padding: '8px 12px', borderRadius: 8,
          background: industry.color + '22', fontSize: 13, fontWeight: 600, color: industry.color,
          textAlign: 'center'
        }}>
          {lang === 'ar' ? industry.nameAr : industry.name}
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '12px 8px', overflow: 'auto' }}>
          {NAV.map((item, i) => {
            if (item.id === '__divider__') return <div key={i} style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '8px 8px' }} />;
            const Icon = item.icon;
            const active = page === item.id;
            return (
              <button key={item.id} onClick={() => setPage(item.id)} style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: active ? 600 : 400,
                background: active ? 'rgba(91,45,142,0.2)' : 'transparent',
                color: active ? '#B794D4' : '#9CA3AF',
                transition: 'all 0.2s', position: 'relative', textAlign: isRtl ? 'right' : 'left',
                fontFamily: 'inherit'
              }}>
                <Icon size={20} stroke={1.8} />
                <span>{t[item.key]}</span>
                {item.pulse && (
                  <span style={{
                    position: 'absolute', top: 10, [isRtl ? 'left' : 'right']: 12,
                    width: 8, height: 8, borderRadius: '50%', background: '#22C55E',
                    animation: 'pulse 2s infinite'
                  }} />
                )}
              </button>
            );
          })}
        </nav>

        {/* Demo controls */}
        <div style={{ padding: '12px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ fontSize: 11, color: '#6B7280', fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>
            {t.demoControls}
          </div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            <button onClick={startDemo} disabled={demoPhase > 0 && demoPhase < 4} style={{
              flex: 1, padding: '8px 0', borderRadius: 6, border: 'none', cursor: 'pointer',
              background: demoPhase === 0 ? '#5B2D8E' : demoPhase === 4 ? '#22C55E' : '#374151',
              color: '#fff', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
              opacity: (demoPhase > 0 && demoPhase < 4) ? 0.5 : 1
            }}>
              {demoPhase === 0 ? t.startDemo : demoPhase === 4 ? t.complete : `${t.salesFlow}...`}
            </button>
            <button onClick={resetDemo} style={{
              padding: '8px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)',
              background: 'transparent', color: '#9CA3AF', cursor: 'pointer'
            }}>
              <IconRotateClockwise size={16} />
            </button>
          </div>

          {/* Phase progress */}
          <div style={{ display: 'flex', gap: 3 }}>
            {[1,2,3].map(p => (
              <div key={p} style={{
                flex: 1, height: 3, borderRadius: 2,
                background: demoPhase >= p ? (demoPhase > p ? '#22C55E' : '#5B2D8E') : 'rgba(255,255,255,0.1)',
                transition: 'all 0.5s'
              }} />
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
            {phaseLabels.map((l, i) => (
              <span key={i} style={{ fontSize: 9, color: demoPhase >= i + 1 ? '#B794D4' : '#4B5563' }}>{l}</span>
            ))}
          </div>

          {/* Agent status */}
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 10, color: '#6B7280', marginBottom: 6 }}>{t.agents}</div>
            {Object.entries(AGENTS).map(([key, agent]) => {
              const AIcon = AGENT_ICONS[key];
              const isActive = true; // All agents always online in demo
              return (
                <div key={key} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 12,
                  color: isActive ? agent.color : '#6B7280'
                }}>
                  <div style={{
                    width: 6, height: 6, borderRadius: '50%',
                    background: isActive ? '#22C55E' : '#374151',
                    animation: isActive ? 'pulse 1.5s infinite' : 'none'
                  }} />
                  <AIcon size={14} stroke={1.5} />
                  <span>{lang === 'ar' ? agent.nameAr : agent.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      </aside>

      {/* Main area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Top bar */}
        <header style={{
          height: 56, background: '#fff', borderBottom: '1px solid #E5E7EB',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 24px', flexShrink: 0
        }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1A1A2E' }}>
            {t[page === 'interactive' ? 'interactiveDemo' : page] || t.dashboard}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={onChangeIndustry} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6,
              border: '1px solid #E5E7EB', background: '#fff', cursor: 'pointer', fontSize: 13, color: '#6B7280',
              fontFamily: 'inherit'
            }}>
              <IconRefresh size={16} /> {t.changeIndustry}
            </button>

            {/* Lang toggle pill */}
            <button onClick={onToggleLang} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 20,
              border: '1px solid #E5E7EB', background: '#F7F2FC', cursor: 'pointer', fontSize: 13,
              fontWeight: 600, color: '#5B2D8E', fontFamily: 'inherit'
            }}>
              <IconLanguage size={16} />
              {lang === 'ar' ? 'EN' : 'عربي'}
            </button>

            <div style={{ position: 'relative' }}>
              <IconBell size={20} color="#6B7280" stroke={1.8} style={{ cursor: 'pointer' }} />
              {notifications.length > 0 && (
                <span style={{
                  position: 'absolute', top: -4, right: -4, width: 8, height: 8,
                  borderRadius: '50%', background: '#EF4444'
                }} />
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <main style={{ flex: 1, overflow: 'auto', padding: 24 }}>
          {renderPage()}
        </main>
      </div>

      {/* Notifications */}
      <div style={{ position: 'fixed', top: 16, [isRtl ? 'left' : 'right']: 16, zIndex: 1000, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {notifications.map(n => (
          <div key={n.id} style={{
            background: '#1A1A2E', color: '#fff', padding: '12px 20px', borderRadius: 10,
            fontSize: 13, fontWeight: 500, boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            animation: 'notifSlide 0.3s ease', maxWidth: 320,
            borderLeft: isRtl ? 'none' : '3px solid #5B2D8E',
            borderRight: isRtl ? '3px solid #5B2D8E' : 'none'
          }}>
            {n.message}
          </div>
        ))}
      </div>
    </div>
  );
}
